import './ViewPitch.css';
import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { HiOutlineArrowLeft, HiOutlineX } from 'react-icons/hi';
import { HiOutlineEye, HiOutlineLink } from 'react-icons/hi2';
import { TbChartBar, TbBuildingStore, TbFilter } from 'react-icons/tb';
import Spinner from '../../components/Spinner/Spinner';
import BrandLogo from '../../components/BrandsTable/BrandLogo';
import { openLink } from '../../utils/openLink';
import {
  fetchExportedOpportunity,
  updateExportedOpportunitySharing,
  updateExportedOpportunityMapConfig,
  MapConfig,
  DEFAULT_MAP_CONFIG,
} from '../../services/exportedOpportunitiesService';

function ViewPitch() {
  const navigate = useNavigate();
  const { opportunityId } = useParams<{ opportunityId: string }>();
  const [loading, setLoading] = useState(true);
  const [stores, setStores] = useState<any[]>([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);

  // Map config state
  const [mapConfig, setMapConfig] = useState<MapConfig>(DEFAULT_MAP_CONFIG);

  // Share state (inline)
  const [shareSaving, setShareSaving] = useState(false);
  const [savedEmails, setSavedEmails] = useState<string[]>([]);
  const [savedDomains, setSavedDomains] = useState<string[]>([]);
  const [shareMessage, setShareMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [emails, setEmails] = useState<string[]>([]);
  const [emailInput, setEmailInput] = useState('');
  const [emailError, setEmailError] = useState('');
  const [allowedDomains, setAllowedDomains] = useState<Set<string>>(new Set());

  useEffect(() => {
    const load = async () => {
      if (!opportunityId) {
        setError('No opportunity ID provided');
        setLoading(false);
        return;
      }
      const record = await fetchExportedOpportunity(opportunityId);
      if (record && record.poi_ids) {
        setStores(Array.isArray(record.poi_ids) ? record.poi_ids : []);
        if (record.allowed_emails) {
          setSavedEmails(record.allowed_emails);
          setEmails(record.allowed_emails);
        }
        if (record.allowed_domains) {
          setSavedDomains(record.allowed_domains);
          setAllowedDomains(new Set(record.allowed_domains));
        }
        if (record.map_config) setMapConfig(record.map_config);
      } else {
        setError('No exported opportunity found');
      }
      setLoading(false);
    };
    load();
  }, [opportunityId]);

  const brandName = useMemo(() => {
    if (stores.length === 0) return '';
    return stores[0]?.brand_name || '';
  }, [stores]);

  // Config helpers
  const updateKpis = (key: keyof MapConfig['include_kpis'], value: boolean) => {
    setMapConfig((prev) => ({
      ...prev,
      include_kpis: { ...prev.include_kpis, [key]: value },
    }));
  };

  const updateNeighbourhood = (key: keyof MapConfig['neighbourhood_stores'], value: any) => {
    setMapConfig((prev) => ({
      ...prev,
      neighbourhood_stores: { ...prev.neighbourhood_stores, [key]: value },
    }));
  };

  const updateFilters = (key: keyof MapConfig['include_filters'], value: boolean) => {
    setMapConfig((prev) => ({
      ...prev,
      include_filters: { ...prev.include_filters, [key]: value },
    }));
  };

  const handleSaveConfig = async () => {
    if (!opportunityId) return;
    setSaving(true);
    setSaveMessage(null);
    const result = await updateExportedOpportunityMapConfig(opportunityId, mapConfig);
    if (result.success) {
      setSaveMessage({ type: 'success', text: 'Configuration saved successfully' });
    } else {
      setSaveMessage({ type: 'error', text: result.message || 'Failed to save configuration' });
    }
    setSaving(false);
    setTimeout(() => setSaveMessage(null), 5000);
  };

  // Share helpers
  const allDomains = useMemo(() => {
    const domains = new Set<string>();
    emails.forEach((email) => {
      const domain = email.split('@')[1];
      if (domain) domains.add(domain);
    });
    return Array.from(domains).sort();
  }, [emails]);

  const shareHasChanges = useMemo(() => {
    const emailsChanged = JSON.stringify([...emails].sort()) !== JSON.stringify([...savedEmails].sort());
    const domainsChanged = JSON.stringify(Array.from(allowedDomains).sort()) !== JSON.stringify([...savedDomains].sort());
    return emailsChanged || domainsChanged;
  }, [emails, allowedDomains, savedEmails, savedDomains]);

  const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const addEmail = (value: string) => {
    const email = value.trim().toLowerCase();
    if (!email) return;
    if (!isValidEmail(email)) { setEmailError('Please enter a valid email address'); return; }
    if (emails.includes(email)) { setEmailError('This email is already added'); return; }
    setEmails([...emails, email]);
    setEmailInput('');
    setEmailError('');
  };

  const handleEmailKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addEmail(emailInput);
    } else if (e.key === 'Backspace' && !emailInput && emails.length > 0) {
      setEmails(emails.slice(0, -1));
    }
  };

  const handleEmailPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text');
    const pastedEmails = pasted.split(/[,;\s]+/).filter(Boolean);
    const newEmails = [...emails];
    pastedEmails.forEach((em) => {
      const trimmed = em.trim().toLowerCase();
      if (isValidEmail(trimmed) && !newEmails.includes(trimmed)) newEmails.push(trimmed);
    });
    setEmails(newEmails);
    setEmailInput('');
    setEmailError('');
  };

  const removeEmail = (email: string) => {
    const updated = emails.filter((e) => e !== email);
    setEmails(updated);
    const domain = email.split('@')[1];
    const domainStillExists = updated.some((e) => e.split('@')[1] === domain);
    if (!domainStillExists) {
      const newDomains = new Set(allowedDomains);
      newDomains.delete(domain);
      setAllowedDomains(newDomains);
    }
  };

  const toggleDomain = (domain: string) => {
    const newDomains = new Set(allowedDomains);
    if (newDomains.has(domain)) newDomains.delete(domain);
    else newDomains.add(domain);
    setAllowedDomains(newDomains);
  };

  const handleShareSave = async () => {
    if (!opportunityId) return;
    setShareSaving(true);
    setShareMessage(null);
    const result = await updateExportedOpportunitySharing(opportunityId, emails, Array.from(allowedDomains));
    if (result.success) {
      setSavedEmails(emails);
      setSavedDomains(Array.from(allowedDomains));
      setShareMessage({ type: 'success', text: 'Sharing settings updated successfully' });
    } else {
      setShareMessage({ type: 'error', text: result.message || 'Failed to update sharing settings' });
    }
    setShareSaving(false);
    setTimeout(() => setShareMessage(null), 5000);
  };

  const handlePreview = () => {
    openLink(`/shared/${opportunityId}`);
  };

  const handleCopyLink = () => {
    const url = `${window.location.origin}/shared/${opportunityId}`;
    navigator.clipboard.writeText(url);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="view-pitch-page">
        <div className="vp-loader">
          <Spinner size="large" />
          <p className="vp-loader-text">Loading opportunity...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="view-pitch-page">
      {/* Header */}
      <div className="vp-header">
        <div className="vp-header-left">
          <button className="vp-back-btn" onClick={() => navigate(-1)}>
            <HiOutlineArrowLeft /> Back
          </button>
          <div style={{ width: 1, height: 24, background: '#e5e7eb' }} />
          {brandName && <BrandLogo brandName={brandName} />}
          <div className="vp-title">
            <h1>{brandName || 'Exported Opportunity'}</h1>
            <p className="vp-subtitle">{stores.length} stores</p>
          </div>
        </div>
        <div className="vp-header-right">
          <button className="vp-copy-link-btn" onClick={handleCopyLink}>
            <HiOutlineLink /> {linkCopied ? 'Copied!' : 'Copy Link'}
          </button>
          <button className="vp-preview-btn" onClick={handlePreview}>
            <HiOutlineEye /> Preview
          </button>
        </div>
      </div>

      {error && <div className="vp-error">{error}</div>}

      {/* Share Section - above config */}
      <div className="vp-share-section">
        <div className="vp-config-section-title">Share Pitch</div>
        <p className="vp-share-description">Add email addresses of people you want to share this pitch with. You can also allow entire domains.</p>

        {shareMessage && (
          <div className={`vp-share-inline-message ${shareMessage.type}`}>{shareMessage.text}</div>
        )}

        <div className="vp-share-input-section">
          <label className="vp-share-label">Allowed Emails</label>
          <div className={`vp-share-email-wrapper ${emailError ? 'error' : ''}`}>
            <div className="vp-share-email-chips">
              {emails.map((email) => (
                <span key={email} className="vp-share-chip">
                  {email}
                  <button type="button" className="vp-share-chip-remove" onClick={() => removeEmail(email)}>
                    <HiOutlineX />
                  </button>
                </span>
              ))}
              <input
                type="text"
                className="vp-share-email-input"
                placeholder={emails.length === 0 ? 'Enter email address...' : ''}
                value={emailInput}
                onChange={(e) => { setEmailInput(e.target.value); setEmailError(''); }}
                onKeyDown={handleEmailKeyDown}
                onPaste={handleEmailPaste}
                onBlur={() => { if (emailInput.trim()) addEmail(emailInput); }}
              />
            </div>
          </div>
          {emailError && <span className="vp-share-input-error">{emailError}</span>}
          <span className="vp-share-input-hint">Press Enter or comma to add</span>
        </div>

        {allDomains.length > 0 && (
          <div className="vp-share-domains-section">
            <label className="vp-share-label">Allow Domains</label>
            <div className="vp-share-domains-list">
              {allDomains.map((domain) => (
                <label key={domain} className="vp-share-domain-item">
                  <input
                    type="checkbox"
                    checked={allowedDomains.has(domain)}
                    onChange={() => toggleDomain(domain)}
                  />
                  <span>Allow anyone with <strong>@{domain}</strong></span>
                </label>
              ))}
            </div>
          </div>
        )}

        <div className="vp-config-actions">
          <button className="vp-save-config-btn" onClick={handleShareSave} disabled={shareSaving || !shareHasChanges}>
            {shareSaving ? 'Saving...' : 'Save Sharing Settings'}
          </button>
        </div>
      </div>

      {/* Map Configuration */}
      <div className="vp-map-config">
        <div className="vp-config-section-title">Map Configuration</div>

        <div className="vp-config-panels">
          {/* Include KPIs */}
          <div className={`vp-config-panel ${!mapConfig.include_kpis.enabled ? 'disabled' : ''}`}>
            <div className="vp-config-panel-header">
              <span className="vp-config-panel-title"><TbChartBar /> Include KPIs</span>
              <button className={`vp-toggle ${mapConfig.include_kpis.enabled ? 'active' : ''}`} onClick={() => updateKpis('enabled', !mapConfig.include_kpis.enabled)} />
            </div>
            <div className="vp-config-item">Total Sites <button className={`vp-toggle ${mapConfig.include_kpis.enabled && mapConfig.include_kpis.total_sites ? 'active' : ''}`} disabled={!mapConfig.include_kpis.enabled} onClick={() => updateKpis('total_sites', !mapConfig.include_kpis.total_sites)} /></div>
            <div className="vp-config-item">Total Neighbourhood Incidents <button className={`vp-toggle ${mapConfig.include_kpis.enabled && mapConfig.include_kpis.total_neighbourhood_incidents ? 'active' : ''}`} disabled={!mapConfig.include_kpis.enabled} onClick={() => updateKpis('total_neighbourhood_incidents', !mapConfig.include_kpis.total_neighbourhood_incidents)} /></div>
            <div className="vp-config-item">Sites in High Crime Area <button className={`vp-toggle ${mapConfig.include_kpis.enabled && mapConfig.include_kpis.sites_in_high_crime_area ? 'active' : ''}`} disabled={!mapConfig.include_kpis.enabled} onClick={() => updateKpis('sites_in_high_crime_area', !mapConfig.include_kpis.sites_in_high_crime_area)} /></div>
          </div>

          {/* Neighbourhood Stores */}
          <div className={`vp-config-panel ${!mapConfig.neighbourhood_stores.enabled ? 'disabled' : ''}`}>
            <div className="vp-config-panel-header">
              <span className="vp-config-panel-title"><TbBuildingStore /> Neighbourhood Stores</span>
              <button className={`vp-toggle ${mapConfig.neighbourhood_stores.enabled ? 'active' : ''}`} onClick={() => updateNeighbourhood('enabled', !mapConfig.neighbourhood_stores.enabled)} />
            </div>
            <div className="vp-config-radio-group">
              <div className="vp-config-radio-label">Display Mode</div>
              <label className="vp-config-radio">
                <input type="radio" name="displayMode" checked={mapConfig.neighbourhood_stores.display_mode === 'show_as_logos'} disabled={!mapConfig.neighbourhood_stores.enabled} onChange={() => updateNeighbourhood('display_mode', 'show_as_logos')} /> Show as Logos
              </label>
              <label className="vp-config-radio">
                <input type="radio" name="displayMode" checked={mapConfig.neighbourhood_stores.display_mode === 'anonymise_stores'} disabled={!mapConfig.neighbourhood_stores.enabled} onChange={() => updateNeighbourhood('display_mode', 'anonymise_stores')} /> Anonymise Stores
              </label>
            </div>
            <div className="vp-config-radio-group">
              <div className="vp-config-radio-label">Neighbourhood Channel</div>
              <label className="vp-config-radio">
                <input type="radio" name="neighborChannel" checked={mapConfig.neighbourhood_stores.neighbourhood_channel === 'show_all'} disabled={!mapConfig.neighbourhood_stores.enabled} onChange={() => updateNeighbourhood('neighbourhood_channel', 'show_all')} /> Show All Channels
              </label>
              <label className="vp-config-radio">
                <input type="radio" name="neighborChannel" checked={mapConfig.neighbourhood_stores.neighbourhood_channel === 'show_same'} disabled={!mapConfig.neighbourhood_stores.enabled} onChange={() => updateNeighbourhood('neighbourhood_channel', 'show_same')} /> Show Same Channel
              </label>
            </div>
            <div className="vp-config-radio-group">
              <div className="vp-config-radio-label">Neighbourhood Store Type</div>
              <label className="vp-config-radio">
                <input type="radio" name="neighborStoreType" checked={(mapConfig.neighbourhood_stores.neighbourhood_store_type || 'all') === 'all'} disabled={!mapConfig.neighbourhood_stores.enabled} onChange={() => updateNeighbourhood('neighbourhood_store_type', 'all')} /> All Neighbourhood Stores
              </label>
              <label className="vp-config-radio">
                <input type="radio" name="neighborStoreType" checked={mapConfig.neighbourhood_stores.neighbourhood_store_type === 'customer'} disabled={!mapConfig.neighbourhood_stores.enabled} onChange={() => updateNeighbourhood('neighbourhood_store_type', 'customer')} /> Only Customer Stores
              </label>
              <label className="vp-config-radio">
                <input type="radio" name="neighborStoreType" checked={mapConfig.neighbourhood_stores.neighbourhood_store_type === 'non_customer'} disabled={!mapConfig.neighbourhood_stores.enabled} onChange={() => updateNeighbourhood('neighbourhood_store_type', 'non_customer')} /> Only Non Customer Stores
              </label>
            </div>
            <div className="vp-config-radio-group">
              <div className="vp-config-radio-label">Installed Solutions</div>
              {['Purchek', 'Cart Containment', 'Cart Manager', 'Subscription', 'Paired Wheel'].map((sol) => {
                const disabledByWhitespace = (mapConfig.neighbourhood_stores.whitespace || []).includes(sol);
                return (
                  <label className="vp-config-radio" key={`installed-${sol}`} title={disabledByWhitespace ? `${sol} is already selected in Whitespace` : ''}>
                    <input type="checkbox" checked={(mapConfig.neighbourhood_stores.installed_solutions || []).includes(sol)} disabled={!mapConfig.neighbourhood_stores.enabled || disabledByWhitespace} onChange={() => {
                      const current = mapConfig.neighbourhood_stores.installed_solutions || [];
                      const updated = current.includes(sol) ? current.filter((s) => s !== sol) : [...current, sol];
                      updateNeighbourhood('installed_solutions', updated);
                    }} /> {sol}
                  </label>
                );
              })}
            </div>
            <div className="vp-config-radio-group">
              <div className="vp-config-radio-label">Whitespace</div>
              {['Purchek', 'Cart Containment', 'Cart Manager', 'Subscription', 'Paired Wheel'].map((sol) => {
                const disabledByInstalled = (mapConfig.neighbourhood_stores.installed_solutions || []).includes(sol);
                return (
                  <label className="vp-config-radio" key={`whitespace-${sol}`} title={disabledByInstalled ? `${sol} is already selected in Installed Solutions` : ''}>
                    <input type="checkbox" checked={(mapConfig.neighbourhood_stores.whitespace || []).includes(sol)} disabled={!mapConfig.neighbourhood_stores.enabled || disabledByInstalled} onChange={() => {
                      const current = mapConfig.neighbourhood_stores.whitespace || [];
                      const updated = current.includes(sol) ? current.filter((s) => s !== sol) : [...current, sol];
                      updateNeighbourhood('whitespace', updated);
                    }} /> {sol}
                  </label>
                );
              })}
            </div>
          </div>

          {/* Include Filters */}
          <div className={`vp-config-panel ${!mapConfig.include_filters.enabled ? 'disabled' : ''}`}>
            <div className="vp-config-panel-header">
              <span className="vp-config-panel-title"><TbFilter /> Include Filters</span>
              <button className={`vp-toggle ${mapConfig.include_filters.enabled ? 'active' : ''}`} onClick={() => updateFilters('enabled', !mapConfig.include_filters.enabled)} />
            </div>
            <div className="vp-config-item">Customer Type <button className={`vp-toggle ${mapConfig.include_filters.enabled && mapConfig.include_filters.customer_type ? 'active' : ''}`} disabled={!mapConfig.include_filters.enabled} onClick={() => updateFilters('customer_type', !mapConfig.include_filters.customer_type)} /></div>
            <div className="vp-config-item">State <button className={`vp-toggle ${mapConfig.include_filters.enabled && mapConfig.include_filters.state ? 'active' : ''}`} disabled={!mapConfig.include_filters.enabled} onClick={() => updateFilters('state', !mapConfig.include_filters.state)} /></div>
            <div className="vp-config-item">City <button className={`vp-toggle ${mapConfig.include_filters.enabled && mapConfig.include_filters.city ? 'active' : ''}`} disabled={!mapConfig.include_filters.enabled} onClick={() => updateFilters('city', !mapConfig.include_filters.city)} /></div>
            <div className="vp-config-item">Recommended Solution <button className={`vp-toggle ${mapConfig.include_filters.enabled && mapConfig.include_filters.recommended_solution ? 'active' : ''}`} disabled={!mapConfig.include_filters.enabled} onClick={() => updateFilters('recommended_solution', !mapConfig.include_filters.recommended_solution)} /></div>
            <div className="vp-config-item">Installed Solution <button className={`vp-toggle ${mapConfig.include_filters.enabled && mapConfig.include_filters.installed_solution ? 'active' : ''}`} disabled={!mapConfig.include_filters.enabled} onClick={() => updateFilters('installed_solution', !mapConfig.include_filters.installed_solution)} /></div>
            <div className="vp-config-item">Crime Index <button className={`vp-toggle ${mapConfig.include_filters.enabled && mapConfig.include_filters.crime_index ? 'active' : ''}`} disabled={!mapConfig.include_filters.enabled} onClick={() => updateFilters('crime_index', !mapConfig.include_filters.crime_index)} /></div>
            <div className="vp-config-item">Weather Condition <button className={`vp-toggle ${mapConfig.include_filters.enabled && mapConfig.include_filters.weather_condition ? 'active' : ''}`} disabled={!mapConfig.include_filters.enabled} onClick={() => updateFilters('weather_condition', !mapConfig.include_filters.weather_condition)} /></div>
            <div className="vp-config-item">Shopping Complex <button className={`vp-toggle ${mapConfig.include_filters.enabled && mapConfig.include_filters.shopping_complex ? 'active' : ''}`} disabled={!mapConfig.include_filters.enabled} onClick={() => updateFilters('shopping_complex', !mapConfig.include_filters.shopping_complex)} /></div>
            <div className="vp-config-item">Cart Ordinance <button className={`vp-toggle ${mapConfig.include_filters.enabled && mapConfig.include_filters.cart_ordinance ? 'active' : ''}`} disabled={!mapConfig.include_filters.enabled} onClick={() => updateFilters('cart_ordinance', !mapConfig.include_filters.cart_ordinance)} /></div>
          </div>
        </div>

        {/* Save Config Button */}
        <div className="vp-config-actions">
          <button className="vp-save-config-btn" onClick={handleSaveConfig} disabled={saving}>
            {saving ? 'Saving...' : 'Save Configuration'}
          </button>
          {saveMessage && (
            <span className={`vp-save-message ${saveMessage.type}`}>{saveMessage.text}</span>
          )}
        </div>
      </div>

    </div>
  );
}

export default ViewPitch;
